import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Document, pdfjs } from 'react-pdf'

import { Highlight } from '@/api/services/highlight'
import { PDF_RESOURCE_OPTIONS } from '@/lib/pdf-config'

import { usePageSwipe } from '../../hooks/usePageSwipe'
import { usePdfScrollLock } from '../../hooks/usePdfScrollLock'
import { buildHighlightAreas } from '../highlight-areas'
import { DualPageGroup } from './pdf-dual-page-group'
import { PdfPage } from './pdf-page'
import { DUAL_GAP, PdfSettings, ScrollMode, SpecialZoomLevel, TextDirection } from './pdf-settings'

// disableStream / disableAutoFetch:
//   デフォルト (false) だと pdf.js は「ファイル全体の progressive stream」と
//   「必要箇所の Range リクエスト」を並行して走らせるため、同じバイトを二重に
//   ダウンロードして転送量がファイルサイズを超える (47.5MB の PDF で 63MB 等)。
//   Range のみに寄せて必要な分だけ取得する。
//   rangeChunkSize はデフォルト (64KB) のままとする。読み込み時のページ辞書の取得や
//   サーバーからサイズが取れなかった時の全ページ getPage() は散発アクセスになるため、
//   チャンクを大きくすると 1 アクセスあたりの無駄な取得量が増えて逆効果になる。
const DOCUMENT_OPTIONS = {
  ...PDF_RESOURCE_OPTIONS,
  disableStream: true,
  disableAutoFetch: true,
}

type PdfDocumentProps = {
  file: string
  settings: PdfSettings
  /** 進行方向と交差する向きのスクロールを止め、オンにした時点の位置に固定する */
  scrollLock?: boolean
  /** ページめくりで次のページへ動かすときにアニメーションするか */
  animatePageTurn?: boolean
  initialPage?: number
  /** サーバーが求めた全ページのサイズ。ページ数が一致すれば pdf.js でのサイズ取得を省く */
  knownPageSizes?: { width: number; height: number }[] | null
  highlights: Highlight[]
  searchKeyword?: string
  searchMatchIndex?: number
  /** アクティブな検索マッチがあるページ */
  searchMatchPage?: number
  onPageChange: (pageIndex: number) => void
  /** 読み込み完了時。検索など他機能から再ダウンロードせずに使い回せるよう proxy も渡す */
  onDocumentLoad: (numPages: number, pdf: pdfjs.PDFDocumentProxy) => void
  /** ファイルの取得状況。Range 取得のたびに呼ばれる */
  onLoadProgress?: (progress: { loaded: number; total: number }) => void
  onHighlightClick: (highlight: Highlight, e: React.MouseEvent) => void
  /** テキスト選択完了時のコールバック */
  onTextSelect?: (
    selectedText: string,
    areas: { pageIndex: number; left: number; top: number; width: number; height: number }[],
  ) => void
  /** ピンチズームでズームレベルが変更された時のコールバック */
  onZoomChange?: (zoom: number) => void
  /** 幅/縦に合わせる を数値ズームに換算した倍率。ズームボタンの起点に使う */
  onEffectiveZoomChange?: (zoom: number) => void
}

// 可視ページの前後にバッファするページ数（スクロール到達前にレンダリング完了させる）
const PAGE_BUFFER = 4
// スクロール方向（進行方向）に追加でバッファするページ数
const PAGE_BUFFER_AHEAD = 3
// モバイル用（メモリ圧を抑えるため縮小）
const PAGE_BUFFER_MOBILE = 2
const PAGE_BUFFER_AHEAD_MOBILE = 2

// ズーム制約定数
const MIN_ZOOM = 0.3
const MAX_ZOOM = 5.0

// ページ周りに残す余白 (px)
const H_MARGIN = 16
const V_MARGIN = 40

// ページめくり中にホイールで 1 ページずつ進める間隔 (ms)。トラックパッドの連続イベントで飛ばしすぎないようにする
const WHEEL_TURN_INTERVAL_MS = 400

// モバイル判定（iPad の Retina で pdf.js の canvas サイズが 4 倍になるのを避ける）
const IS_MOBILE =
  typeof navigator !== 'undefined' &&
  (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    // iPadOS 13+ は Mac として判定される場合があるので touch で補完
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent)))

export function PdfDocument({
  file,
  settings,
  scrollLock = false,
  animatePageTurn = true,
  initialPage,
  knownPageSizes,
  highlights,
  searchKeyword,
  searchMatchIndex,
  searchMatchPage,
  onPageChange,
  onDocumentLoad,
  onLoadProgress,
  onHighlightClick,
  onTextSelect,
  onZoomChange,
  onEffectiveZoomChange,
}: PdfDocumentProps) {
  const [numPages, setNumPages] = useState(0)
  // 各ページの実寸サイズ (width / height, scale=1)。ページ番号 0-indexed
  const [pageSizes, setPageSizes] = useState<Map<number, { width: number; height: number }>>(new Map())
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 })
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set())
  const [currentPage, setCurrentPage] = useState(initialPage ?? 0)
  const [pdfProxy, setPdfProxy] = useState<pdfjs.PDFDocumentProxy | null>(null)
  // 初期ページの描画が終わるまでは他ページの描画・サイズ取得を止め、Range 取得を初期ページに集中させる
  const [isInitialPageRendered, setIsInitialPageRendered] = useState(false)
  const handleInitialPageRendered = useCallback(() => setIsInitialPageRendered(true), [])

  // 代表サイズ (基準スケール計算用)
  const defaultPageSize = useMemo(() => pageSizes.get(0) ?? null, [pageSizes])

  const containerRef = useRef<HTMLDivElement>(null)
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const observerRef = useRef<IntersectionObserver | null>(null)
  const isInitialScrollDone = useRef(false)
  const currentPageRef = useRef(currentPage)
  const suppressPageTrackingRef = useRef(false)
  // スクロール方向検出用（進行方向を優先的にプリロード）
  const scrollDirectionRef = useRef<1 | -1>(1)
  const lastScrollPosRef = useRef(0)
  // 現在ページ内のどこを読んでいるか（0=ページ先頭, 0.5=ページ中央）。ズーム/リサイズ後の位置復元に使う
  const pageOffsetRatioRef = useRef(0)
  // ピンチズーム確定後の scroll 位置補正データ (touchend → settings.zoom 反映後の useEffect で消費)
  const pendingScrollAdjustRef = useRef<{
    ratio: number
    centerX: number
    centerY: number
    oldDocOffsetLeft: number
    oldDocOffsetTop: number
  } | null>(null)

  const isHorizontal = settings.scrollMode === ScrollMode.Horizontal
  const isRtl = settings.direction === TextDirection.RightToLeft
  const applyLockedAxis = usePdfScrollLock(containerRef, scrollLock, isHorizontal)

  // コンテナサイズ監視
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // file 変更や再ロード時に、進行中の getPage ループをキャンセルするためのトークン
  const documentLoadTokenRef = useRef(0)

  // ドキュメントロード: 最初のページのサイズだけ取得して表示の基準にする
  const handleDocumentLoad = useCallback(
    async (pdf: pdfjs.PDFDocumentProxy) => {
      const token = ++documentLoadTokenRef.current
      const np = pdf.numPages
      setNumPages(np)
      onDocumentLoad(np, pdf)
      if (knownPageSizes?.length === np) return

      try {
        const first = await pdf.getPage(1)
        if (documentLoadTokenRef.current > token) return
        const vp = first.getViewport({ scale: 1 })
        setPageSizes(new Map([[0, { width: vp.width, height: vp.height }]]))
        setPdfProxy(pdf)
      } catch {
        // 基準サイズが取れなくても描画は止めない
        if (documentLoadTokenRef.current > token) return
        setIsInitialPageRendered(true)
      }
    },
    [onDocumentLoad, knownPageSizes],
  )

  // 初期ページ描画後に、残りページの実寸サイズを並列取得して placeholder のズレを防ぐ
  useEffect(() => {
    if (!pdfProxy || !isInitialPageRendered) return

    let cancelled = false
    const np = pdfProxy.numPages
    const CONCURRENCY = 8
    ;(async () => {
      for (let start = 2; start <= np; start += CONCURRENCY) {
        if (cancelled) return
        const batch: Promise<{ index: number; width: number; height: number } | null>[] = []
        for (let i = 0; i < CONCURRENCY && start + i <= np; i++) {
          const pageNum = start + i
          batch.push(
            pdfProxy
              .getPage(pageNum)
              .then((p) => {
                const vp = p.getViewport({ scale: 1 })
                return { index: pageNum - 1, width: vp.width, height: vp.height }
              })
              .catch(() => null),
          )
        }
        const results = await Promise.all(batch)
        if (cancelled) return
        setPageSizes((prev) => {
          const next = new Map(prev)
          for (const r of results) {
            if (r) next.set(r.index, { width: r.width, height: r.height })
          }
          return next
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [pdfProxy, isInitialPageRendered])

  // file 変更時にサイズマップをリセット + 進行中のロードをキャンセル
  useEffect(() => {
    documentLoadTokenRef.current++
    setPageSizes(new Map())
    setPdfProxy(null)
    setIsInitialPageRendered(false)
  }, [file])

  // サーバー由来のサイズを反映（上のリセットより後に実行されるようここに置く）
  useEffect(() => {
    if (!knownPageSizes?.length) return
    // 読み込んだ PDF とページ数が合わなければ別ファイルの情報なので使わない
    if (numPages > 0 && knownPageSizes.length !== numPages) return
    setPageSizes(new Map(knownPageSizes.map((size, i) => [i, size])))
  }, [file, knownPageSizes, numPages])

  // 描画サイズ計算（代表サイズ基準）
  // pageWidth は見開きなら 2 ページ分の行幅、pageHeight は 1 ページの高さ
  // 横スクロールは常に高さを揃える（幅を揃えるとページ毎に寸法が違う PDF で高さがバラバラになって見にくい）
  // 数値ズームは縦スクロールでは幅フィット、横スクロールでは幅・高さの両方に収まるサイズを 1.0 とする
  const { pageWidth, pageHeight, effectiveZoom, fitsMainAxis } = useMemo(() => {
    const empty = { pageWidth: undefined, pageHeight: undefined, effectiveZoom: undefined, fitsMainAxis: false }
    if (!defaultPageSize || containerSize.width === 0) return empty

    const zoom = settings.zoom
    const pagesPerRow = settings.viewMode === 'single' ? 1 : 2
    const ratio = defaultPageSize.width / defaultPageSize.height
    const rowWidth = containerSize.width - H_MARGIN - (pagesPerRow - 1) * DUAL_GAP
    const fitHeight = containerSize.height - V_MARGIN
    if (rowWidth <= 0 || fitHeight <= 0) return empty

    // 行の幅がコンテナに収まるときのページ高さ
    const widthFitHeight = rowWidth / pagesPerRow / ratio
    const baseHeight = isHorizontal ? Math.min(widthFitHeight, fitHeight) : widthFitHeight
    const presetHeight = zoom === SpecialZoomLevel.PageWidth ? widthFitHeight : fitHeight
    // ページめくりではプリセットでも進行方向に 1 行が収まる大きさまで縮める
    // （iPhone の縦持ちで横スクロールにすると、縦に合わせたページは画面幅より広くなる）
    const mainAxisFitHeight = isHorizontal ? widthFitHeight : fitHeight
    const targetHeight =
      typeof zoom === 'number'
        ? baseHeight * zoom
        : settings.dragMode === 'snap'
          ? Math.min(presetHeight, mainAxisFitHeight)
          : presetHeight
    const factor = targetHeight / baseHeight
    // 進行方向に 1 行が画面に収まるか（収まらないとページめくりでページの途中を読めない）
    const fitsMainAxis = isHorizontal
      ? targetHeight * ratio * pagesPerRow + (pagesPerRow - 1) * DUAL_GAP <= containerSize.width + 1
      : targetHeight <= containerSize.height + 1

    // 縦に合わせる場合は各ページの高さを揃え、代表ページと寸法が違うページもはみ出さないようにする
    if (isHorizontal || zoom === SpecialZoomLevel.PageHeight) {
      return { pageWidth: undefined, pageHeight: targetHeight, effectiveZoom: factor, fitsMainAxis }
    }
    return { pageWidth: rowWidth * factor, pageHeight: undefined, effectiveZoom: factor, fitsMainAxis }
  }, [defaultPageSize, containerSize, settings.zoom, settings.viewMode, settings.dragMode, isHorizontal])

  // ページめくり。ピンチで画面より大きくしている間は自由にスクロールさせる
  const isSnap = settings.dragMode === 'snap' && fitsMainAxis
  const isInstantTurn = isSnap && !animatePageTurn

  const effectiveZoomRef = useRef(effectiveZoom)
  useEffect(() => {
    effectiveZoomRef.current = effectiveZoom
    if (effectiveZoom !== undefined) onEffectiveZoomChange?.(effectiveZoom)
  }, [effectiveZoom, onEffectiveZoomChange])

  // CSS変数でページ比率を設定（プレースホルダー用フォールバック）
  const cssVars = useMemo(() => {
    if (!defaultPageSize) return {}
    return { '--pdf-page-ratio': String(defaultPageSize.width / defaultPageSize.height) } as React.CSSProperties
  }, [defaultPageSize])

  // 各ページの実寸に基づくプレースホルダーサイズを算出
  const getPageDimensions = useCallback(
    (pageIndex: number) => {
      // 該当ページのサイズが取得済みならそれを、なければ代表サイズにフォールバック
      const size = pageSizes.get(pageIndex) ?? defaultPageSize
      if (!size) return { estimatedWidth: undefined, estimatedHeight: undefined }
      if (pageHeight) {
        return {
          estimatedWidth: pageHeight * (size.width / size.height),
          estimatedHeight: pageHeight,
        }
      }
      if (pageWidth) {
        return {
          estimatedWidth: pageWidth,
          estimatedHeight: pageWidth / (size.width / size.height),
        }
      }
      return { estimatedWidth: size.width, estimatedHeight: size.height }
    },
    [pageSizes, defaultPageSize, pageWidth, pageHeight],
  )

  // Intersection Observer でページ可視性追跡
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setVisiblePages((prev) => {
          let changed = false
          const next = new Set(prev)
          for (const entry of entries) {
            const idx = Number(entry.target.getAttribute('data-page-observer'))
            if (Number.isNaN(idx)) continue
            if (entry.isIntersecting) {
              if (!next.has(idx)) {
                next.add(idx)
                changed = true
              }
            } else if (next.has(idx)) {
              next.delete(idx)
              changed = true
            }
          }
          // 変更がなければ同じ参照を返して再レンダリングを回避
          return changed ? next : prev
        })
      },
      {
        root: containerRef.current,
        threshold: 0,
        // スクロール到達前に PDF レンダリングを完了させるため大きめに取る
        // モバイルはメモリ圧を抑えるため控えめに
        rootMargin: IS_MOBILE ? '400px' : '800px',
      },
    )

    observerRef.current = observer

    // 既存要素を観察
    for (const [, el] of pageRefs.current) {
      observer.observe(el)
    }

    return () => observer.disconnect()
  }, [numPages])

  // スクロール位置からページを追跡（rAF スロットル）
  useEffect(() => {
    const container = containerRef.current
    if (!container || numPages === 0) return

    let rafId: number | null = null

    const runPageTracking = () => {
      rafId = null
      if (suppressPageTrackingRef.current) return

      // スクロール方向を更新（進行方向のプリロードに使用）
      const currentScrollPos = isHorizontal ? container.scrollLeft : container.scrollTop
      const delta = currentScrollPos - lastScrollPosRef.current
      if (delta !== 0) {
        scrollDirectionRef.current = delta >= 0 ? 1 : -1
        lastScrollPosRef.current = currentScrollPos
      }

      const containerRect = container.getBoundingClientRect()
      const anchor = isHorizontal ? containerRect.left : containerRect.top
      let bestPage = currentPageRef.current
      let bestDistance = Infinity
      let bestRect: DOMRect | null = null

      for (const [pageIdx, el] of pageRefs.current) {
        const rect = el.getBoundingClientRect()
        const distance = isHorizontal ? Math.abs(rect.left - anchor) : Math.abs(rect.top - anchor)

        if (distance < bestDistance) {
          bestDistance = distance
          bestPage = pageIdx
          bestRect = rect
        }
      }

      // ページ内の読んでいる位置を比率で記録（ズーム/リサイズでレイアウトが変わっても復元できるように）
      // 横スクロールは1ページ単位でめくる想定なので常にページ先頭 (0) を基準にする
      pageOffsetRatioRef.current =
        !isHorizontal && bestRect && bestRect.height > 0 ? (containerRect.top - bestRect.top) / bestRect.height : 0

      if (bestPage !== currentPageRef.current) {
        currentPageRef.current = bestPage
        setCurrentPage(bestPage)
        onPageChange(bestPage)
      }
    }

    const handleScroll = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(runPageTracking)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [numPages, isHorizontal, onPageChange])

  // ページ要素のrefコールバック
  const setPageRef = useCallback(
    (pageIndex: number) => (el: HTMLDivElement | null) => {
      if (el) {
        pageRefs.current.set(pageIndex, el)
        observerRef.current?.observe(el)
      } else {
        const prev = pageRefs.current.get(pageIndex)
        if (prev) observerRef.current?.unobserve(prev)
        pageRefs.current.delete(pageIndex)
      }
    },
    [],
  )

  // 初期ページへスクロール（代表サイズが確定しプレースホルダー高さが正確になってから実行）
  useEffect(() => {
    // initialPage は 0 も有効値なので undefined だけを弾く（!initialPage だと 0 が弾かれて
    // 初期スクロール完了フラグが立たず、後続の設定変更復元 useEffect が動かなくなる）
    if (isInitialScrollDone.current || initialPage === undefined || numPages === 0 || !defaultPageSize) return
    // ページ追跡を抑制（初期スクロール中にcurrentPageRefが0に上書きされるのを防止）
    suppressPageTrackingRef.current = true
    // レイアウト確定後にスクロール（2フレーム待つことでDOM反映を確実に）
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = pageRefs.current.get(initialPage)
        if (el) {
          el.scrollIntoView({ behavior: 'instant' })
        }
        applyLockedAxis()
        isInitialScrollDone.current = true
        // 初期ページをcurrentPageに反映（フッター・スクロールバー更新）
        currentPageRef.current = initialPage
        setCurrentPage(initialPage)
        onPageChange(initialPage)
        // スクロール完了後にページ追跡を再開
        requestAnimationFrame(() => {
          suppressPageTrackingRef.current = false
        })
      })
    })
  }, [initialPage, numPages, defaultPageSize, onPageChange, applyLockedAxis])

  // ページジャンプ関数（外部から呼び出し用）
  // 固定中の横スクロールは nearest にして、画面より高いページで上下位置を動かさない
  const jumpToPage = useCallback(
    (pageIndex: number) => {
      const el = pageRefs.current.get(pageIndex)
      if (el) {
        el.scrollIntoView({
          behavior: isInstantTurn ? 'instant' : 'smooth',
          block: scrollLock && isHorizontal ? 'nearest' : 'start',
        })
      }
    },
    [scrollLock, isHorizontal, isInstantTurn],
  )

  // ページめくりで隣のページ（見開きは隣の組）へ移る
  const turnPage = useCallback(
    (step: number) => {
      const keys = [...pageRefs.current.keys()].sort((a, b) => a - b)
      const current = keys.indexOf(currentPageRef.current)
      const next = current + step
      if (current < 0 || next < 0 || next >= keys.length) return
      const target = keys.at(next)
      if (target === undefined) return
      pageRefs.current.get(target)?.scrollIntoView({
        behavior: isInstantTurn ? 'instant' : 'smooth',
        inline: isHorizontal ? 'center' : 'nearest',
        block: isHorizontal ? 'nearest' : 'center',
      })
    },
    [isHorizontal, isInstantTurn],
  )
  const turnToNextPage = useCallback(() => turnPage(1), [turnPage])
  const turnToPrevPage = useCallback(() => turnPage(-1), [turnPage])

  usePageSwipe({
    targetRef: containerRef,
    enabled: isInstantTurn,
    axis: isHorizontal ? 'x' : 'y',
    reversed: isHorizontal && isRtl,
    onNext: turnToNextPage,
    onPrev: turnToPrevPage,
  })

  // 外部からjumpToPageを呼べるようにrefに格納（PdfReaderで使用）
  useEffect(() => {
    if (containerRef.current) {
      ;(containerRef.current as HTMLDivElement & { jumpToPage?: (p: number) => void }).jumpToPage = jumpToPage
    }
  }, [jumpToPage])

  // 設定変更・リサイズ時に現在ページ位置を復元
  // (direction/scrollMode/viewMode/zoom の変更や画面サイズ変更でページの描画サイズが変わると
  //  スクロール位置がページ境界からずれて「読んでいたページが変わる」ため)
  // 依存は containerSize/zoom そのものではなく描画サイズにする。こうすると縦スクロール中に
  // モバイルのアドレスバー伸縮でコンテナ高さだけ変わったケースでは発火しない。
  useEffect(() => {
    if (!isInitialScrollDone.current) return
    // ピンチズーム由来の変更はピンチ中心を保つ専用の補正が走るのでスキップ
    if (pendingScrollAdjustRef.current) return
    const page = currentPageRef.current
    const offsetRatio = pageOffsetRatioRef.current

    // スクロールイベントによるページ追跡を一時抑制（リセット中の誤検知防止）
    suppressPageTrackingRef.current = true

    // レイアウト変更完了後にスクロール復元
    // (リサイズ中は本 effect が連続発火するが、cleanup の clearTimeout でデバウンスされる)
    const timer = setTimeout(() => {
      const el = pageRefs.current.get(page)
      if (el) {
        el.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'start' })
        // ページ内の読んでいた位置まで戻す（縦スクロールでズーム時にページが画面より大きい場合に有効）
        // 比率は縦スクロール時のみ記録しているので、横スクロールでは適用しない
        const container = containerRef.current
        if (container && !isHorizontal && offsetRatio > 0) {
          container.scrollTop += offsetRatio * el.getBoundingClientRect().height
        }
      }
      applyLockedAxis()
      // スクロール完了後にページ追跡を再開
      requestAnimationFrame(() => {
        suppressPageTrackingRef.current = false
      })
    }, 100)
    return () => {
      clearTimeout(timer)
      suppressPageTrackingRef.current = false
    }
  }, [isRtl, isHorizontal, settings.scrollMode, settings.viewMode, pageWidth, pageHeight, applyLockedAxis])

  // ページが可視かどうか判定（バッファ込み + スクロール方向に追加プリロード）
  const isPageVisible = useCallback(
    (pageIndex: number) => {
      if (!isInitialPageRendered) {
        // 基準サイズ確定前に描画すると、サイズ確定後に描き直しになる
        if (!defaultPageSize || numPages === 0) return false
        const target = Math.min(Math.max(initialPage ?? 0, 0), numPages - 1)
        // 見開きは相方のページも同時に出す
        return settings.viewMode === 'single' ? pageIndex === target : Math.abs(pageIndex - target) <= 1
      }
      if (visiblePages.has(pageIndex)) return true
      const direction = scrollDirectionRef.current
      const bufBase = IS_MOBILE ? PAGE_BUFFER_MOBILE : PAGE_BUFFER
      const bufAhead = IS_MOBILE ? PAGE_BUFFER_AHEAD_MOBILE : PAGE_BUFFER_AHEAD
      for (const vp of visiblePages) {
        const diff = pageIndex - vp
        // 進行方向は bufBase + bufAhead 追加、逆方向は bufBase のみ
        const ahead = direction > 0 ? bufBase + bufAhead : bufBase
        const behind = direction < 0 ? bufBase + bufAhead : bufBase
        if (diff >= 0 && diff <= ahead) return true
        if (diff < 0 && -diff <= behind) return true
      }
      // 現在ページは常に可視にする（ズーム変更時のIO空白期間対策）
      if (pageIndex === currentPageRef.current) return true
      // 初期ページ周辺も可視にする（初期スクロール先が仮想化で消えないように）
      if (initialPage !== undefined && !isInitialScrollDone.current && Math.abs(pageIndex - initialPage) <= bufBase)
        return true
      // 初期ロード時は最初の数ページを表示
      if (visiblePages.size === 0 && pageIndex < 3) return true
      return false
    },
    [visiblePages, initialPage, isInitialPageRendered, defaultPageSize, numPages, settings.viewMode],
  )

  // 検索ハイライト用 customTextRenderer (react-pdf v10: HTML文字列を返す)
  const searchHighlightRenderer = useMemo(() => {
    if (!searchKeyword) return undefined
    const keyword = searchKeyword.toLowerCase()
    return (props: { str: string }) => {
      const text = props.str
      if (!text) return text
      const lower = text.toLowerCase()
      if (!lower.includes(keyword)) return text
      // マッチ箇所を <mark> でラップ (HTML文字列として返す)
      const escaped = text.replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
      )
      // 大文字小文字を無視してマッチ部分を分割・再構築
      const lowerEscaped = escaped.toLowerCase()
      const escapedKeyword = keyword.replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
      )
      const lowerKey = escapedKeyword.toLowerCase()
      const parts: string[] = []
      let searchFrom = 0
      let foundIdx = lowerEscaped.indexOf(lowerKey, searchFrom)
      while (foundIdx !== -1) {
        parts.push(escaped.slice(searchFrom, foundIdx))
        const matchText = escaped.slice(foundIdx, foundIdx + lowerKey.length)
        parts.push(
          `<mark class="pdf-search-mark" style="background-color:rgba(255,255,0,0.4);border-radius:2px">${matchText}</mark>`,
        )
        searchFrom = foundIdx + lowerKey.length
        foundIdx = lowerEscaped.indexOf(lowerKey, searchFrom)
      }
      parts.push(escaped.slice(searchFrom))
      return parts.join('')
    }
  }, [searchKeyword])

  // 選択中の検索マッチをオレンジでハイライト
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // 前回のアクティブスタイルをリセット
    for (const mark of container.querySelectorAll<HTMLElement>('mark.pdf-search-mark-active')) {
      mark.style.backgroundColor = 'rgba(255,255,0,0.4)'
      mark.classList.remove('pdf-search-mark-active')
    }

    if (searchMatchPage == null || searchMatchIndex == null || searchMatchIndex < 0) return

    // 対象ページのページ要素を取得
    const pageEl = container.querySelector(`[data-page-index="${searchMatchPage}"]`)
    if (!pageEl) return

    const marks = pageEl.querySelectorAll<HTMLElement>('mark.pdf-search-mark')
    const target = marks.item(searchMatchIndex)
    if (target) {
      target.style.backgroundColor = 'rgba(255,165,0,0.7)'
      target.classList.add('pdf-search-mark-active')
      // スクロール中のページ追跡を一時抑制（再レンダリングによるスクロール位置の揺れ防止）
      suppressPageTrackingRef.current = true
      const targetRect = target.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const offset = targetRect.top - containerRect.top + container.scrollTop
      container.scrollTo({ top: offset, behavior: 'smooth' })
      // smooth スクロール完了後にページ追跡を再開
      const timer = setTimeout(() => {
        suppressPageTrackingRef.current = false
      }, 500)
      return () => {
        clearTimeout(timer)
        suppressPageTrackingRef.current = false
      }
    }
  }, [searchMatchPage, searchMatchIndex, searchKeyword])

  // テキスト選択ハンドラー
  useEffect(() => {
    if (!onTextSelect) return
    const container = containerRef.current
    if (!container) return

    const handleMouseUp = () => {
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed || !selection.rangeCount) return

      const selectedText = selection.toString().trim()
      if (!selectedText) return

      try {
        const range = selection.getRangeAt(0)
        // ページごとに矩形を割り当てるため、ページをまたぐ選択にも対応する
        const areas = buildHighlightAreas(range)
        if (areas.length === 0) return

        onTextSelect(selectedText, areas)
      } catch {
        // 選択エラーは無視
      }
    }

    container.addEventListener('mouseup', handleMouseUp)
    return () => container.removeEventListener('mouseup', handleMouseUp)
  }, [onTextSelect])

  // ピンチズーム (タッチデバイス)
  // touchmove 中は CSS transform で即座にプレビューし、pdf.js の再ラスタライズは touchend で 1 回だけ行う。
  // これにより「ピンチしても 10% だけ動いて止まる」現象を回避する。
  useEffect(() => {
    if (!onZoomChange) return
    const container = containerRef.current
    if (!container) return

    const state = {
      active: false,
      initialDistance: 0,
      initialZoom: 1,
      currentScale: 1,
      pinchCenter: { x: 0, y: 0 },
      initialScroll: { left: 0, top: 0 },
      docEl: null as HTMLElement | null,
      // docEl の container コンテンツ左上からのオフセット (safe center で中央寄せされると > 0 になる)。
      // transform-origin は docEl 自身の座標系で指定する必要があるため保持する。
      docOffset: { left: 0, top: 0 },
    }

    const getDistance = (t1: Touch, t2: Touch) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
    const getCenter = (t1: Touch, t2: Touch) => ({
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2,
    })

    const resetTransform = () => {
      if (state.docEl) {
        state.docEl.style.transform = ''
        state.docEl.style.transformOrigin = ''
        state.docEl.style.willChange = ''
      }
    }

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return
      const docEl = container.querySelector<HTMLElement>('.react-pdf__Document')
      if (!docEl) return
      const rect = container.getBoundingClientRect()
      const center = getCenter(e.touches[0], e.touches[1])
      state.active = true
      state.initialDistance = getDistance(e.touches[0], e.touches[1])
      state.initialZoom = typeof settings.zoom === 'number' ? settings.zoom : (effectiveZoomRef.current ?? 1)
      state.currentScale = 1
      state.pinchCenter = { x: center.x - rect.left, y: center.y - rect.top }
      state.initialScroll = { left: container.scrollLeft, top: container.scrollTop }
      // safe center などで docEl が中央寄せされている場合の container コンテンツ座標系での docEl 位置
      state.docOffset = { left: docEl.offsetLeft, top: docEl.offsetTop }
      state.docEl = docEl
      // GPU コンポジット層に昇格させて transform を軽くする
      docEl.style.willChange = 'transform'
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!state.active || e.touches.length !== 2) return
      e.preventDefault()
      const currentDistance = getDistance(e.touches[0], e.touches[1])
      const rawScale = currentDistance / state.initialDistance
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, state.initialZoom * rawScale))
      const actualScale = newZoom / state.initialZoom
      state.currentScale = actualScale

      if (!state.docEl) return
      // container コンテンツ座標系のピンチ中心を docEl 内座標系に変換 (docEl.offsetLeft を差し引く)
      const originX = state.initialScroll.left + state.pinchCenter.x - state.docOffset.left
      const originY = state.initialScroll.top + state.pinchCenter.y - state.docOffset.top
      state.docEl.style.transformOrigin = `${originX}px ${originY}px`
      state.docEl.style.transform = `scale(${actualScale})`
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (!state.active) return
      // まだ2本以上残っていれば継続
      if (e.touches.length >= 2) return
      const scale = state.currentScale
      state.active = false

      // ほぼ変化なし → transform を消すだけ
      if (Math.abs(scale - 1) < 0.01) {
        resetTransform()
        return
      }

      const rawZoom = state.initialZoom * scale
      const roundedZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(rawZoom * 100) / 100))
      // 丸めで initialZoom と同値になる場合は useEffect が発火しないため即リセット
      if (roundedZoom === state.initialZoom) {
        resetTransform()
        return
      }
      // 新 zoom で pdf.js が再描画された後の scroll 補正データを保存
      // (settings.zoom 変化を検知する useEffect で消費 & transform リセット)
      // oldDocOffset も包む → ズーム前後で safe center 中央寄せ ↔ overflow 左寄せに切り替わってもピンチ中心を正しく保つ
      pendingScrollAdjustRef.current = {
        ratio: roundedZoom / state.initialZoom,
        centerX: state.pinchCenter.x,
        centerY: state.pinchCenter.y,
        oldDocOffsetLeft: state.docOffset.left,
        oldDocOffsetTop: state.docOffset.top,
      }
      onZoomChange(roundedZoom)
    }

    // touchcancel は残指数に関わらず必ず終了扱いにする (willChange/transform リーク防止)
    const handleTouchCancel = () => {
      if (!state.active) return
      state.active = false
      resetTransform()
    }

    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })
    container.addEventListener('touchcancel', handleTouchCancel, { passive: true })
    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('touchcancel', handleTouchCancel)
      // ピンチ確定直後 (pendingScrollAdjustRef 有り) は scroll 補正 useEffect が rAF 内で
      // transform をリセットするため、cleanup で先にリセットすると新 zoom ラスタライズ完了前に
      // 一瞬 1.0x へ戻る視覚フラッシュが起きる。pending が無い経路 (外部 zoom 変更等) のみ
      // GPU レイヤーリーク防止のためリセットする。
      if (!pendingScrollAdjustRef.current) {
        resetTransform()
      }
    }
  }, [onZoomChange, settings.zoom])

  // ピンチズーム確定後: 新 zoom による pdf.js 再描画に合わせて scroll 位置を補正し、CSS transform をリセット
  useEffect(() => {
    const adj = pendingScrollAdjustRef.current
    if (!adj) return
    pendingScrollAdjustRef.current = null
    const container = containerRef.current
    if (!container) return

    // pageWidth prop 更新 → PdfPage placeholder リサイズ → container.scrollWidth 反映は
    // React の commit フェーズ後だが、レイアウトが確実に完了してから scroll 補正するため 1 フレーム待つ。
    // (scrollWidth が旧サイズだと大きい scrollLeft/Top が自動クランプされて補正が破綻する)
    const { ratio, centerX, centerY, oldDocOffsetLeft, oldDocOffsetTop } = adj
    const rafId = requestAnimationFrame(() => {
      const docEl = container.querySelector<HTMLElement>('.react-pdf__Document')
      const newDocOffsetLeft = docEl?.offsetLeft ?? 0
      const newDocOffsetTop = docEl?.offsetTop ?? 0
      // ピンチ中心がスクリーン上の同じ位置に留まるよう scroll 補正。
      // docEl 内座標系でのピンチ中心 = (scrollLeft + centerX - oldDocOffsetLeft) を ratio 倍し、
      // 新たな docEl 位置を加えて container 座標系に戻す。
      const newScrollLeft = newDocOffsetLeft + ratio * (container.scrollLeft + centerX - oldDocOffsetLeft) - centerX
      const newScrollTop = newDocOffsetTop + ratio * (container.scrollTop + centerY - oldDocOffsetTop) - centerY

      if (docEl) {
        docEl.style.transform = ''
        docEl.style.transformOrigin = ''
        docEl.style.willChange = ''
      }
      container.scrollLeft = newScrollLeft
      container.scrollTop = newScrollTop
      applyLockedAxis()

      // scroll 変化でページ追跡が誤発火しないよう抑制
      suppressPageTrackingRef.current = true
      requestAnimationFrame(() => {
        suppressPageTrackingRef.current = false
      })
    })
    return () => cancelAnimationFrame(rafId)
  }, [settings.zoom, applyLockedAxis])

  // マウスホイール: 横スクロールは水平に動かし、ページめくりは 1 回で隣のページへ移る
  useEffect(() => {
    if (!isHorizontal && !isSnap) return
    const container = containerRef.current
    if (!container) return

    // direction:rtl では scrollLeft の正方向が視覚的に逆になる
    const forward = isRtl ? -1 : 1
    let lastTurnAt = 0
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return
      e.preventDefault()
      if (!isSnap) {
        container.scrollLeft += forward * e.deltaY
        return
      }
      // 少しずつ動かすと元のページに吸着し直すため、ホイール 1 回で 1 ページずつ進める
      const now = performance.now()
      if (now - lastTurnAt < WHEEL_TURN_INTERVAL_MS) return
      lastTurnAt = now
      turnPage(Math.sign(e.deltaY))
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  }, [isRtl, isHorizontal, isSnap, turnPage])

  // コンテナのスタイル（shorthand overflow を使わず longhand で統一し React 警告を回避）
  const containerStyle = useMemo((): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: '100%',
      ...cssVars,
      ...(isSnap && { scrollSnapType: isHorizontal ? 'x mandatory' : 'y mandatory' }),
    }

    // スクロール固定中は交差軸をスクロールさせない（位置は usePdfScrollLock が Document をずらして出す）
    // アニメーションなしのページめくりは進行方向を指で動かさせず、usePageSwipe で一瞬で移る
    if (isHorizontal) {
      return {
        ...base,
        overflowX: isInstantTurn ? 'hidden' : 'auto',
        // ズームで page 高さが container を超えたときにも縦スクロールできるよう auto
        overflowY: scrollLock ? 'hidden' : 'auto',
        whiteSpace: 'nowrap',
        direction: isRtl ? 'rtl' : 'ltr',
      }
    }

    return {
      ...base,
      overflowX: scrollLock ? 'hidden' : 'auto',
      overflowY: isInstantTurn ? 'hidden' : 'auto',
    }
  }, [isHorizontal, isRtl, cssVars, scrollLock, isSnap, isInstantTurn])

  // ページラッパーのスタイル
  const pageWrapperStyle = useMemo<React.CSSProperties>(() => {
    const snap: React.CSSProperties = isSnap ? { scrollSnapAlign: 'center', scrollSnapStop: 'always' } : {}
    if (isHorizontal) {
      return {
        display: 'inline-block',
        verticalAlign: 'top',
        direction: 'ltr', // ページ内コンテンツは常にLTR描画
        ...snap,
      }
    }
    // 縦モードで page 幅が container 幅を超えるとき (ズーム時) に横スクロールできるよう
    // `safe center` を指定する。通常の `center` だと子が親を超えた瞬間に左側に押し出され
    // scrollLeft が最左端まで届かなくなる (flex/grid の既知挙動)。
    // さらに `minWidth: max-content` で flex コンテナ自身を子コンテンツ幅まで拡張して、
    // 親 (.pdf-document-container) の scrollWidth に overflow を伝播させる。
    return {
      display: 'flex',
      justifyContent: 'safe center',
      minWidth: 'max-content',
      ...snap,
    }
  }, [isHorizontal, isSnap])

  // Dual Page レンダリング
  const renderDualPages = useMemo(() => {
    if (settings.viewMode === 'single') return null

    const pairs: number[][] = []
    let i = 0

    // DualPageWithCover: 最初のページは単独
    if (settings.viewMode === 'dual-cover' && numPages > 0) {
      pairs.push([0])
      i = 1
    }

    while (i < numPages) {
      if (i + 1 < numPages) {
        pairs.push([i, i + 1])
      } else {
        pairs.push([i])
      }
      i += 2
    }

    return pairs
  }, [settings.viewMode, numPages])

  return (
    <div ref={containerRef} style={containerStyle} className="pdf-document-container">
      <Document
        file={file}
        onLoadSuccess={handleDocumentLoad}
        onLoadProgress={onLoadProgress}
        options={DOCUMENT_OPTIONS}
        loading={<div className="flex items-center justify-center h-full text-muted-foreground">読み込み中...</div>}
        error={
          <div className="flex items-center justify-center h-full text-destructive">PDFの読み込みに失敗しました</div>
        }
      >
        {settings.viewMode === 'single'
          ? // シングルページモード
            Array.from({ length: numPages }, (_, i) => {
              const dims = getPageDimensions(i)
              return (
                <div key={i} ref={setPageRef(i)} data-page-observer={i} style={pageWrapperStyle}>
                  <PdfPage
                    pageIndex={i}
                    width={pageWidth}
                    height={pageHeight}
                    isVisible={isPageVisible(i)}
                    highlights={highlights}
                    estimatedWidth={dims.estimatedWidth}
                    estimatedHeight={dims.estimatedHeight}
                    searchHighlightRenderer={searchHighlightRenderer}
                    onHighlightClick={onHighlightClick}
                    onRenderSettled={handleInitialPageRendered}
                  />
                </div>
              )
            })
          : // デュアルページモード
            renderDualPages?.map((pair, groupIdx) => (
              <DualPageGroup
                key={groupIdx}
                pair={pair}
                pageWrapperStyle={pageWrapperStyle}
                isRtl={isRtl}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
                getPageDimensions={getPageDimensions}
                isPageVisible={isPageVisible}
                highlights={highlights}
                searchHighlightRenderer={searchHighlightRenderer}
                onHighlightClick={onHighlightClick}
                onRenderSettled={handleInitialPageRendered}
                setPageRef={setPageRef}
              />
            ))}
      </Document>
    </div>
  )
}
